/**
 * Planner — where task work is placed on real time, and where the honest
 * answer about what did not fit is given.
 *
 * TWO TABS. "Agenda" is a day-grouped chronological list; "Matrix" is the
 * priority/impact grid. There is no time grid and no drag surface anywhere on
 * this page: the repo has no calendar component, and the `setupTests.ts`
 * ResizeObserver stub never fires its callback, so anything width-measured
 * would render nothing under test (see components/BackfillTimeline for the
 * same trap and the same measurement-free answer). A list is also the shape
 * that can say "this one is not yours to move" in words.
 *
 * THREE KINDS OF THING SHARE THE AGENDA, and the difference is the point:
 *   • task blocks          — yours; this page moves, keeps, releases them.
 *   • commitment occurrences — yours, but they travel only through their own
 *     respond/reschedule machinery in Commitments. Nothing here moves one.
 *   • imported events      — somebody else's calendar, read-only truth. The
 *     app cannot move them and must never imply otherwise.
 *
 * NOTHING IS WRITTEN WITHOUT A PREVIEW FIRST. Both mutating flows (schedule
 * and push) ask the server what *would* happen, render every part of that
 * answer including the infeasibilities, and only then offer a second, explicit
 * Apply — the idiom CheckIn's reschedule dialog established. The server
 * recomputes on apply rather than replaying the preview, which is stated in
 * the dialog rather than hidden.
 *
 * A 409 mentioning the timezone means the ACCOUNT has no IANA zone, not that
 * the request was bad. Planning is a question about future wall-clock times,
 * so there is no offset fallback and no guess: the page says what to set and
 * where, and never shows a confident empty agenda instead.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import EisenhowerMatrix, { MatrixTask } from '../components/EisenhowerMatrix';
import PageShell from '../components/PageShell';
import { useLayoutTokens } from '../hooks/useLayoutTokens';
import { getApiErrorMessage } from '../utils/apiError';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/** Today plus six — one week of agenda without asking the user anything. */
const DEFAULT_WINDOW_DAYS = 7;
const WINDOW_CHOICES = [3, 7, 14];

/** Clock tick for the Now/Next marks. Minute resolution is plenty. */
const CLOCK_TICK_MS = 30000;

// ---------------------------------------------------------------------------
// Wire shapes (schemas.py: TaskBlockResponse, TaskPlanResponse, …)
// ---------------------------------------------------------------------------

export type TaskBlockState = 'planned' | 'kept' | 'pushed' | 'released';

export interface TaskBlock {
  id: number;
  user_id: number;
  task_id: number;
  start_at: string;
  end_at: string;
  minutes: number;
  state: string;
  pushed_from_block_id: number | null;
  released_note: string | null;
  task_title: string | null;
  project_id: number | null;
  project_title: string | null;
}

export interface CommitmentOccurrence {
  id: number;
  commitment_id: number;
  planned_start_at: string;
  planned_end_at: string;
  state: string;
  title: string | null;
  reschedulable?: boolean;
}

export interface PlannedEvent {
  id: number;
  title: string | null;
  location: string | null;
  start_time: string;
  end_time: string | null;
  all_day: boolean;
  status: string;
  source: string;
}

export interface TaskPlanBlock {
  task_id: number;
  start_at: string;
  end_at: string;
  minutes: number;
  is_late: boolean;
  replaces_block_id: number | null;
  block_id: number | null;
  task_title: string | null;
  project_title: string | null;
}

export interface TaskPlanPlacement {
  task_id: number;
  task_title: string | null;
  project_title: string | null;
  estimated_minutes: number | null;
  kept_minutes: number;
  requested_minutes: number;
  placed_minutes: number;
  unplaced_minutes: number;
  late_minutes: number;
  infeasibility: string;
  reason: string;
}

export interface TaskPlanResponse {
  applied: boolean;
  timezone: string;
  now: string;
  horizon_days: number;
  horizon_end: string;
  requested_minutes: number;
  placed_minutes: number;
  unplaced_minutes: number;
  late_minutes: number;
  placements: TaskPlanPlacement[];
  blocks: TaskPlanBlock[];
  unconfirmed_past_blocks: TaskBlock[];
  replaced_block_ids: number[];
  created_block_ids: number[];
  warnings: string[];
}

export interface TaskPlanShortenedBlock {
  block_id: number;
  new_end_at: string;
  minutes: number;
}

export interface TaskPlanCommitmentPointer {
  occurrence_id: number;
  commitment_id: number;
  title: string | null;
  planned_start_at: string;
  planned_end_at: string;
  reschedulable: boolean;
}

export interface TaskPlanPushResponse {
  applied: boolean;
  timezone: string;
  from_at: string;
  until: string;
  horizon_end: string;
  displaced_block_ids: number[];
  shortened_blocks: TaskPlanShortenedBlock[];
  displaced_minutes: number;
  replaced_minutes: number;
  unplaced_minutes: number;
  placements: TaskPlanPlacement[];
  blocks: TaskPlanBlock[];
  created_block_ids: number[];
  commitments_in_window: TaskPlanCommitmentPointer[];
  warnings: string[];
}

// ---------------------------------------------------------------------------
// Formatting. All wall-clock rendering is hand-rolled 24h so the page reads
// the same in every locale and a test can assert a span literally.
// ---------------------------------------------------------------------------

const pad = (value: number): string => String(value).padStart(2, '0');

const formatClock = (value: Date): string =>
  `${pad(value.getHours())}:${pad(value.getMinutes())}`;

const localDayKey = (value: Date): string =>
  `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;

const startOfDay = (value: Date): Date =>
  new Date(value.getFullYear(), value.getMonth(), value.getDate());

const addDays = (value: Date, days: number): Date =>
  new Date(value.getFullYear(), value.getMonth(), value.getDate() + days);

const formatMinutes = (minutes: number): string => {
  const total = Math.abs(Math.round(minutes));
  const hours = Math.floor(total / 60);
  const rest = total % 60;
  const sign = minutes < 0 ? '-' : '';
  if (hours === 0) return `${sign}${rest}m`;
  if (rest === 0) return `${sign}${hours}h`;
  return `${sign}${hours}h ${rest}m`;
};

const formatDate = (value: Date): string =>
  value.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });

const formatInstant = (iso: string): string => {
  const value = new Date(iso);
  if (Number.isNaN(value.getTime())) return iso;
  return `${localDayKey(value)} ${formatClock(value)}`;
};

/** A span, saying so plainly when it crosses midnight. */
const formatSpan = (start: Date, end: Date): string => {
  const span = `${formatClock(start)}–${formatClock(end)}`;
  return localDayKey(start) === localDayKey(end) ? span : `${span} (${localDayKey(end)})`;
};

/** `<input type="datetime-local">` wants local wall clock, not an instant. */
const toLocalInput = (value: Date): string =>
  `${localDayKey(value)}T${pad(value.getHours())}:${pad(value.getMinutes())}`;

/** …and the server wants an instant, so the offset is applied on the way out. */
const fromLocalInput = (value: string): string | null => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
};

const roundUpToQuarter = (value: Date): Date => {
  const copy = new Date(value.getTime());
  copy.setSeconds(0, 0);
  const remainder = copy.getMinutes() % 15;
  if (remainder !== 0) copy.setMinutes(copy.getMinutes() + (15 - remainder));
  return copy;
};

const blockTitle = (block: { task_title: string | null; task_id: number }): string =>
  block.task_title ?? `Task #${block.task_id}`;

const STATE_LABEL: Record<string, string> = {
  planned: 'Planned',
  kept: 'Kept',
  pushed: 'Pushed',
  released: 'Released',
};

const STATE_COLOR: Record<string, 'default' | 'primary' | 'success' | 'warning'> = {
  planned: 'primary',
  kept: 'success',
  pushed: 'warning',
  released: 'default',
};

const stateOf = (block: TaskBlock): string => String(block.state ?? '').toLowerCase();

/**
 * A 409 whose detail talks about the timezone is the account-level "no zone
 * set" case, which has its own actionable empty state. Every other 409 —
 * an overlap, a state refusal — is a real answer about this request and is
 * surfaced verbatim instead.
 */
const isMissingTimezone = (error: any): boolean =>
  error?.response?.status === 409 &&
  /timezone/i.test(String(error?.response?.data?.detail ?? ''));

// ---------------------------------------------------------------------------
// The merged agenda
// ---------------------------------------------------------------------------

type AgendaKind = 'block' | 'commitment' | 'event';

interface AgendaItem {
  key: string;
  kind: AgendaKind;
  start: Date;
  end: Date;
  title: string;
  block?: TaskBlock;
  occurrence?: CommitmentOccurrence;
  event?: PlannedEvent;
}

/** States in which an occurrence still describes time that was really used. */
const LIVE_OCCURRENCE_STATES = ['planned', 'honored', 'partial'];

/**
 * Does this item still occupy the clock? A pushed or released block is
 * history the user made deliberately — it stays visible (hiding it would make
 * a moved block look like a vanished one) but it is not what you are doing
 * now.
 */
const occupiesTime = (item: AgendaItem): boolean => {
  if (item.kind === 'block') {
    const state = stateOf(item.block as TaskBlock);
    return state === 'planned' || state === 'kept';
  }
  if (item.kind === 'commitment') {
    return LIVE_OCCURRENCE_STATES.includes(
      String(item.occurrence?.state ?? '').toLowerCase(),
    );
  }
  return String(item.event?.status ?? '').toLowerCase() !== 'cancelled';
};

const Planner: React.FC = () => {
  const navigate = useNavigate();
  const {
    pageHeaderSx,
    panelPaddingSx,
    sectionGapSx,
    dialogContentPaddingSx,
    tableCellPaddingSx,
  } = useLayoutTokens();

  const [tab, setTab] = useState<'agenda' | 'matrix'>('agenda');
  const [now, setNow] = useState<Date>(() => new Date());
  const [windowDays, setWindowDays] = useState<number>(DEFAULT_WINDOW_DAYS);

  const [blocks, setBlocks] = useState<TaskBlock[]>([]);
  const [occurrences, setOccurrences] = useState<CommitmentOccurrence[]>([]);
  const [events, setEvents] = useState<PlannedEvent[]>([]);
  const [tasks, setTasks] = useState<MatrixTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [loadErrors, setLoadErrors] = useState<string[]>([]);
  const [taskError, setTaskError] = useState<string | null>(null);
  const [timezoneMissing, setTimezoneMissing] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Schedule preview / apply.
  const [planOpen, setPlanOpen] = useState(false);
  const [plan, setPlan] = useState<TaskPlanResponse | null>(null);
  const [planScope, setPlanScope] = useState<MatrixTask | null>(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [planApplying, setPlanApplying] = useState(false);
  const [planError, setPlanError] = useState<string | null>(null);

  // Push preview / apply.
  const [pushOpen, setPushOpen] = useState(false);
  const [pushFrom, setPushFrom] = useState('');
  const [pushUntil, setPushUntil] = useState('');
  const [push, setPush] = useState<TaskPlanPushResponse | null>(null);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushApplying, setPushApplying] = useState(false);
  const [pushError, setPushError] = useState<string | null>(null);

  // Single-block dialogs.
  const [editTarget, setEditTarget] = useState<TaskBlock | null>(null);
  const [editStart, setEditStart] = useState('');
  const [editEnd, setEditEnd] = useState('');
  const [editError, setEditError] = useState<string | null>(null);
  const [releaseTarget, setReleaseTarget] = useState<TaskBlock | null>(null);
  const [releaseNote, setReleaseNote] = useState('');
  const [releaseError, setReleaseError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TaskBlock | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [busyBlockId, setBusyBlockId] = useState<number | null>(null);

  /** Blocks the server told us are past-but-unanswered, by id. */
  const [answeredNeeded, setAnsweredNeeded] = useState<TaskBlock[]>([]);

  const windowStart = useMemo(() => startOfDay(now), [now]);
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), CLOCK_TICK_MS);
    return () => clearInterval(id);
  }, []);

  const loadAgenda = useCallback(async () => {
    setLoading(true);
    const from = startOfDay(new Date());
    const to = addDays(from, windowDays);
    const errors: string[] = [];
    let missingZone = false;

    const [blockResult, occurrenceResult, eventResult] = await Promise.all([
      axios
        .get(`${API_BASE_URL}/api/task-blocks`, {
          params: { from: from.toISOString(), to: to.toISOString() },
        })
        .then(
          (res) => ({ rows: res.data, error: null as any }),
          (error) => ({ rows: null, error }),
        ),
      axios
        .get(`${API_BASE_URL}/api/commitments/occurrences`, {
          params: { from: localDayKey(from), to: localDayKey(addDays(from, windowDays - 1)) },
        })
        .then(
          (res) => ({ rows: res.data, error: null as any }),
          (error) => ({ rows: null, error }),
        ),
      axios
        .get(`${API_BASE_URL}/api/planned-events/`, {
          params: { from: from.toISOString(), to: to.toISOString() },
        })
        .then(
          (res) => ({ rows: res.data, error: null as any }),
          (error) => ({ rows: null, error }),
        ),
    ]);

    // Each source fails on its own: one dead endpoint must not blank the other
    // two, and the agenda says which part is missing rather than looking empty.
    if (blockResult.error) {
      errors.push(getApiErrorMessage(blockResult.error, 'Could not load your task blocks'));
      setBlocks([]);
    } else {
      setBlocks(Array.isArray(blockResult.rows) ? blockResult.rows : []);
    }

    if (occurrenceResult.error) {
      if (isMissingTimezone(occurrenceResult.error)) missingZone = true;
      else {
        errors.push(
          getApiErrorMessage(occurrenceResult.error, 'Could not load your commitments'),
        );
      }
      setOccurrences([]);
    } else {
      setOccurrences(Array.isArray(occurrenceResult.rows) ? occurrenceResult.rows : []);
    }

    if (eventResult.error) {
      errors.push(
        getApiErrorMessage(eventResult.error, 'Could not load your imported events'),
      );
      setEvents([]);
    } else {
      setEvents(Array.isArray(eventResult.rows) ? eventResult.rows : []);
    }

    setTimezoneMissing(missingZone);
    setLoadErrors(errors);
    setLoading(false);
  }, [windowDays]);

  /**
   * Tasks, for the matrix and for the deadline a block can be late against.
   *
   * There is no bulk task read, so this is one call per project. Failure
   * degrades to no matrix and no late markers — never to a guessed deadline.
   */
  const loadTasks = useCallback(async () => {
    setTasksLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/projects/`);
      const projects = Array.isArray(res.data) ? res.data : [];
      const trees = await Promise.all(
        projects.map((project: any) =>
          axios
            .get(`${API_BASE_URL}/api/projects/${project.id}/tree`)
            .then((tree) => tree.data)
            .catch(() => null),
        ),
      );
      const flat: MatrixTask[] = [];
      const walk = (nodes: any[], projectId: number, projectTitle: string) => {
        (Array.isArray(nodes) ? nodes : []).forEach((node) => {
          if (node && typeof node.id === 'number') {
            flat.push({
              id: node.id,
              title: String(node.title ?? `Task #${node.id}`),
              status: node.status ?? null,
              project_id: projectId,
              project_title: projectTitle,
              priority: node.priority ?? null,
              impact: node.impact ?? null,
              estimated_minutes: node.estimated_minutes ?? null,
              due_date: node.due_date ?? null,
            });
            walk(node.children ?? [], projectId, projectTitle);
          }
        });
      };
      trees.forEach((tree: any) => {
        if (tree && typeof tree.id === 'number') {
          walk(tree.tasks ?? [], tree.id, String(tree.title ?? ''));
        }
      });
      setTasks(flat);
      setTaskError(null);
    } catch (err: any) {
      setTasks([]);
      setTaskError(getApiErrorMessage(err, 'Could not load your tasks'));
    } finally {
      setTasksLoading(false);
    }
  }, []);

  // loadAgenda is keyed to the window length only, deliberately: the clock
  // ticks every 30s for the Now/Next marks and must not drag a refetch with it.
  useEffect(() => {
    loadAgenda();
  }, [loadAgenda]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const taskById = useMemo(() => {
    const map = new Map<number, MatrixTask>();
    tasks.forEach((task) => map.set(task.id, task));
    return map;
  }, [tasks]);

  /** Open work only: a done task on the grid is noise, not a decision. */
  const matrixTasks = useMemo(
    () =>
      tasks.filter((task) => {
        const status = String(task.status ?? '').toLowerCase();
        return status !== 'done' && status !== 'cancelled';
      }),
    [tasks],
  );

  /** A block is late when it ends after its task's deadline. */
  const lateAgainstDeadline = useCallback(
    (block: TaskBlock): string | null => {
      const due = taskById.get(block.task_id)?.due_date;
      if (!due) return null;
      const deadline = new Date(due);
      if (Number.isNaN(deadline.getTime())) return null;
      return new Date(block.end_at) > deadline ? formatInstant(due) : null;
    },
    [taskById],
  );

  const items = useMemo<AgendaItem[]>(() => {
    const merged: AgendaItem[] = [];
    blocks.forEach((block) => {
      merged.push({
        key: `block-${block.id}`,
        kind: 'block',
        start: new Date(block.start_at),
        end: new Date(block.end_at),
        title: blockTitle(block),
        block,
      });
    });
    occurrences.forEach((occurrence) => {
      merged.push({
        key: `commitment-${occurrence.id}`,
        kind: 'commitment',
        start: new Date(occurrence.planned_start_at),
        end: new Date(occurrence.planned_end_at),
        title: occurrence.title ?? `Commitment #${occurrence.commitment_id}`,
        occurrence,
      });
    });
    events.forEach((event) => {
      const start = new Date(event.start_time);
      merged.push({
        key: `event-${event.id}`,
        kind: 'event',
        start,
        // An imported event with no end is not an error and not a guess: it is
        // rendered as an instant and labelled as having no stated end.
        end: event.end_time ? new Date(event.end_time) : start,
        title: event.title ?? 'Untitled event',
        event,
      });
    });
    return merged
      .filter((item) => !Number.isNaN(item.start.getTime()))
      .sort((a, b) => a.start.getTime() - b.start.getTime());
  }, [blocks, occurrences, events]);

  const days = useMemo(() => {
    const buckets: { key: string; date: Date; items: AgendaItem[] }[] = [];
    for (let index = 0; index < windowDays; index += 1) {
      const date = addDays(windowStart, index);
      buckets.push({ key: localDayKey(date), date, items: [] });
    }
    const byKey = new Map(buckets.map((bucket) => [bucket.key, bucket]));
    items.forEach((item) => {
      // Something that started before the window still belongs to its first
      // day inside it — dropping it would show a morning that looks free.
      const anchor = item.start < windowStart ? windowStart : item.start;
      byKey.get(localDayKey(anchor))?.items.push(item);
    });
    return buckets;
  }, [items, windowStart, windowDays]);

  const { currentItem, nextItem } = useMemo(() => {
    const live = items.filter(occupiesTime);
    const current =
      live.find((item) => item.start <= now && now < item.end) ?? null;
    const upcoming = live.find((item) => item.start > now) ?? null;
    return { currentItem: current, nextItem: upcoming };
  }, [items, now]);

  /**
   * Past PLANNED blocks: the ones nothing has confirmed. Sourced both from the
   * window read and from whatever the last plan response reported, deduped by
   * id. Neither source is ever resolved automatically — the whole point of the
   * PLANNED state surviving its own end time is that only the user can say
   * whether the sitting happened.
   */
  const needsAnAnswer = useMemo(() => {
    const map = new Map<number, TaskBlock>();
    blocks.forEach((block) => {
      if (stateOf(block) === 'planned' && new Date(block.end_at) <= now) {
        map.set(block.id, block);
      }
    });
    answeredNeeded.forEach((block) => map.set(block.id, block));
    return Array.from(map.values()).sort(
      (a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime(),
    );
  }, [blocks, answeredNeeded, now]);

  const blockById = useMemo(() => {
    const map = new Map<number, TaskBlock>();
    blocks.forEach((block) => map.set(block.id, block));
    answeredNeeded.forEach((block) => map.set(block.id, block));
    return map;
  }, [blocks, answeredNeeded]);

  const describeBlockId = useCallback(
    (id: number): string => {
      const block = blockById.get(id);
      if (!block) return `Block #${id}`;
      return `Block #${id} — ${blockTitle(block)}, ${formatSpan(
        new Date(block.start_at),
        new Date(block.end_at),
      )}`;
    },
    [blockById],
  );

  // -------------------------------------------------------------------------
  // Per-block actions
  // -------------------------------------------------------------------------

  const runBlockAction = async (
    block: TaskBlock,
    request: () => Promise<unknown>,
    success: string,
    fallback: string,
    onError?: (message: string) => void,
  ): Promise<boolean> => {
    setBusyBlockId(block.id);
    setActionError(null);
    try {
      await request();
      // The response body is not trusted as the new truth: a keep or a release
      // changes what a re-plan would do, so the window is re-read.
      await loadAgenda();
      setAnsweredNeeded((previous) =>
        previous.filter((candidate) => candidate.id !== block.id),
      );
      setNotice(success);
      return true;
    } catch (err: any) {
      // The server's own words. A 400/409 here says something specific — "a
      // kept block is history and cannot be deleted", "that span overlaps task
      // block 7" — and paraphrasing it would throw the only useful part away.
      const message = getApiErrorMessage(err, fallback);
      if (onError) onError(message);
      else setActionError(message);
      return false;
    } finally {
      setBusyBlockId(null);
    }
  };

  const keepBlock = (block: TaskBlock) =>
    runBlockAction(
      block,
      () => axios.post(`${API_BASE_URL}/api/task-blocks/${block.id}/keep`),
      `${blockTitle(block)} kept.`,
      'Could not keep that block',
    );

  const submitRelease = async () => {
    if (!releaseTarget) return;
    const note = releaseNote.trim();
    const ok = await runBlockAction(
      releaseTarget,
      () =>
        axios.post(`${API_BASE_URL}/api/task-blocks/${releaseTarget.id}/release`, {
          note: note === '' ? null : note,
        }),
      `${blockTitle(releaseTarget)} released — its minutes are back in the pool.`,
      'Could not release that block',
      setReleaseError,
    );
    if (ok) setReleaseTarget(null);
  };

  const submitDelete = async () => {
    if (!deleteTarget) return;
    const ok = await runBlockAction(
      deleteTarget,
      () => axios.delete(`${API_BASE_URL}/api/task-blocks/${deleteTarget.id}`),
      `${blockTitle(deleteTarget)} unscheduled.`,
      'Could not delete that block',
      setDeleteError,
    );
    if (ok) setDeleteTarget(null);
  };

  const openEdit = (block: TaskBlock) => {
    setEditError(null);
    setEditStart(toLocalInput(new Date(block.start_at)));
    setEditEnd(toLocalInput(new Date(block.end_at)));
    setEditTarget(block);
  };

  const submitEdit = async () => {
    if (!editTarget) return;
    const start = fromLocalInput(editStart);
    const end = fromLocalInput(editEnd);
    if (!start || !end) {
      setEditError('Both ends of the span are needed.');
      return;
    }
    const ok = await runBlockAction(
      editTarget,
      () =>
        axios.put(`${API_BASE_URL}/api/task-blocks/${editTarget.id}`, {
          start_at: start,
          end_at: end,
        }),
      `${blockTitle(editTarget)} moved.`,
      'Could not move that block',
      setEditError,
    );
    if (ok) setEditTarget(null);
  };

  // -------------------------------------------------------------------------
  // Schedule: preview, then apply
  // -------------------------------------------------------------------------

  const planPayload = useCallback(
    (scope: MatrixTask | null) => (scope ? { task_ids: [scope.id] } : {}),
    [],
  );

  const openPlanPreview = useCallback(
    async (scope: MatrixTask | null) => {
      setPlanScope(scope);
      setPlan(null);
      setPlanError(null);
      setPlanOpen(true);
      setPlanLoading(true);
      try {
        const res = await axios.post(
          `${API_BASE_URL}/api/task-blocks/schedule/preview`,
          planPayload(scope),
        );
        const data: TaskPlanResponse = res?.data ?? null;
        setPlan(data);
        if (data) {
          setAnsweredNeeded(data.unconfirmed_past_blocks ?? []);
          setTimezoneMissing(false);
        }
      } catch (err: any) {
        if (isMissingTimezone(err)) {
          setTimezoneMissing(true);
          setPlanOpen(false);
        } else {
          setPlanError(getApiErrorMessage(err, 'Could not work out a plan'));
        }
      } finally {
        setPlanLoading(false);
      }
    },
    [planPayload],
  );

  const applyPlan = async () => {
    if (!plan) return;
    setPlanApplying(true);
    setPlanError(null);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/task-blocks/schedule/apply`,
        planPayload(planScope),
      );
      const data: TaskPlanResponse = res?.data ?? null;
      setAnsweredNeeded(data?.unconfirmed_past_blocks ?? []);
      await loadAgenda();
      setPlanOpen(false);
      setPlan(null);
      const created = data?.created_block_ids?.length ?? 0;
      const unplaced = data?.unplaced_minutes ?? 0;
      setNotice(
        unplaced > 0
          ? `Applied: ${created} block${created === 1 ? '' : 's'} created, ` +
            `${formatMinutes(unplaced)} still has nowhere to go.`
          : `Applied: ${created} block${created === 1 ? '' : 's'} created.`,
      );
    } catch (err: any) {
      if (isMissingTimezone(err)) {
        setTimezoneMissing(true);
        setPlanOpen(false);
      } else {
        setPlanError(getApiErrorMessage(err, 'Could not apply that plan'));
      }
    } finally {
      setPlanApplying(false);
    }
  };

  // -------------------------------------------------------------------------
  // Push: preview, then apply
  // -------------------------------------------------------------------------

  const openPush = () => {
    setPush(null);
    setPushError(null);
    setPushFrom(toLocalInput(roundUpToQuarter(new Date())));
    setPushUntil('');
    setPushOpen(true);
  };

  const pushPayload = (): Record<string, unknown> | null => {
    const from = fromLocalInput(pushFrom);
    if (!from) return null;
    const until = pushUntil ? fromLocalInput(pushUntil) : null;
    return until ? { from_at: from, until } : { from_at: from };
  };

  const previewPush = async () => {
    const payload = pushPayload();
    if (!payload) {
      setPushError('Say when you become unavailable.');
      return;
    }
    setPush(null);
    setPushError(null);
    setPushLoading(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/task-blocks/push/preview`,
        payload,
      );
      setPush(res?.data ?? null);
    } catch (err: any) {
      if (isMissingTimezone(err)) {
        setTimezoneMissing(true);
        setPushOpen(false);
      } else {
        setPushError(getApiErrorMessage(err, 'Could not work out that push'));
      }
    } finally {
      setPushLoading(false);
    }
  };

  const applyPush = async () => {
    const payload = pushPayload();
    if (!payload || !push) return;
    setPushApplying(true);
    setPushError(null);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/task-blocks/push/apply`,
        payload,
      );
      const data: TaskPlanPushResponse = res?.data ?? null;
      await loadAgenda();
      setPushOpen(false);
      setPush(null);
      const moved = data?.displaced_block_ids?.length ?? 0;
      const unplaced = data?.unplaced_minutes ?? 0;
      setNotice(
        unplaced > 0
          ? `Push applied: ${moved} block${moved === 1 ? '' : 's'} displaced, ` +
            `${formatMinutes(unplaced)} could not be re-placed.`
          : `Push applied: ${moved} block${moved === 1 ? '' : 's'} displaced.`,
      );
    } catch (err: any) {
      if (isMissingTimezone(err)) {
        setTimezoneMissing(true);
        setPushOpen(false);
      } else {
        setPushError(getApiErrorMessage(err, 'Could not apply that push'));
      }
    } finally {
      setPushApplying(false);
    }
  };

  // -------------------------------------------------------------------------
  // Rendering
  // -------------------------------------------------------------------------

  const renderPlacements = (placements: TaskPlanPlacement[]) => (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={tableCellPaddingSx}>Task</TableCell>
            <TableCell sx={tableCellPaddingSx}>Asked for</TableCell>
            <TableCell sx={tableCellPaddingSx}>Placed</TableCell>
            <TableCell sx={tableCellPaddingSx}>Unplaced</TableCell>
            <TableCell sx={tableCellPaddingSx}>Late</TableCell>
            <TableCell sx={tableCellPaddingSx}>What the scheduler said</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {placements.map((placement) => (
            <TableRow key={placement.task_id}>
              <TableCell sx={tableCellPaddingSx}>
                {placement.task_title ?? `Task #${placement.task_id}`}
                {placement.project_title && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {placement.project_title}
                  </Typography>
                )}
              </TableCell>
              <TableCell sx={tableCellPaddingSx}>
                {formatMinutes(placement.requested_minutes)}
              </TableCell>
              <TableCell sx={tableCellPaddingSx}>
                {formatMinutes(placement.placed_minutes)}
              </TableCell>
              <TableCell sx={tableCellPaddingSx}>
                {placement.unplaced_minutes > 0 ? (
                  <Typography variant="body2" color="error.main">
                    {formatMinutes(placement.unplaced_minutes)}
                  </Typography>
                ) : (
                  formatMinutes(0)
                )}
              </TableCell>
              <TableCell sx={tableCellPaddingSx}>
                {placement.late_minutes > 0 ? (
                  <Typography variant="body2" color="warning.main">
                    {formatMinutes(placement.late_minutes)}
                  </Typography>
                ) : (
                  formatMinutes(0)
                )}
              </TableCell>
              <TableCell sx={tableCellPaddingSx}>
                {/* The resolver's own reason, unrounded and unparaphrased. */}
                {placement.reason ? (
                  <Typography variant="body2">{placement.reason}</Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Everything fitted.
                  </Typography>
                )}
                {placement.infeasibility && placement.infeasibility !== 'none' && (
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    sx={{ mt: 0.5 }}
                    label={`Blocked by: ${placement.infeasibility}`}
                  />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderPlanBlocks = (planBlocks: TaskPlanBlock[]) => (
    <Stack spacing={0.5} sx={{ mt: 0.5 }}>
      {planBlocks.map((planBlock, index) => (
        <Typography key={`${planBlock.task_id}-${planBlock.start_at}-${index}`} variant="body2">
          {formatSpan(new Date(planBlock.start_at), new Date(planBlock.end_at))} ·{' '}
          {formatMinutes(planBlock.minutes)} — {planBlock.task_title ?? `Task #${planBlock.task_id}`}
          {planBlock.project_title ? ` (${planBlock.project_title})` : ''}
          {planBlock.is_late && (
            <Chip size="small" color="warning" label="Late" sx={{ ml: 1 }} />
          )}
          {planBlock.replaces_block_id !== null &&
            planBlock.replaces_block_id !== undefined && (
              <Typography variant="caption" color="text.secondary" display="block">
                Replaces block #{planBlock.replaces_block_id}
              </Typography>
            )}
        </Typography>
      ))}
    </Stack>
  );

  const renderAnswerRow = (block: TaskBlock) => (
    <Stack
      key={`answer-${block.id}`}
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1}
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      justifyContent="space-between"
      sx={{ py: 0.75 }}
    >
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {blockTitle(block)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatSpan(new Date(block.start_at), new Date(block.end_at))} ·{' '}
          {formatMinutes(block.minutes)}
          {block.project_title ? ` · ${block.project_title}` : ''}
        </Typography>
      </Box>
      <Stack direction="row" spacing={1}>
        <Button
          size="small"
          variant="outlined"
          disabled={busyBlockId === block.id}
          aria-label={`Confirm ${blockTitle(block)} happened`}
          onClick={() => keepBlock(block)}
        >
          It happened
        </Button>
        <Button
          size="small"
          variant="text"
          disabled={busyBlockId === block.id}
          aria-label={`Say ${blockTitle(block)} did not happen`}
          onClick={() => {
            setReleaseNote('');
            setReleaseError(null);
            setReleaseTarget(block);
          }}
        >
          It did not
        </Button>
      </Stack>
    </Stack>
  );

  const renderAgendaItem = (item: AgendaItem) => {
    const isNow = currentItem?.key === item.key;
    const isNext = !isNow && nextItem?.key === item.key;
    const block = item.block;
    const state = block ? stateOf(block) : '';
    const lateDue = block ? lateAgainstDeadline(block) : null;
    const minutesLeft = Math.max(0, Math.round((item.end.getTime() - now.getTime()) / 60000));
    const minutesUntil = Math.max(
      0,
      Math.round((item.start.getTime() - now.getTime()) / 60000),
    );

    return (
      <Paper
        key={item.key}
        variant="outlined"
        data-testid={`agenda-${item.key}`}
        sx={{
          p: 1.25,
          mb: 1,
          borderLeft: 4,
          borderLeftColor:
            item.kind === 'block'
              ? 'primary.main'
              : item.kind === 'commitment'
                ? 'secondary.main'
                : 'divider',
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          spacing={1}
        >
          <Box>
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
              {isNow && <Chip size="small" color="success" label="Now" />}
              {isNext && <Chip size="small" color="info" label="Next" />}
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {item.kind === 'event' && item.event?.all_day
                  ? 'All day'
                  : formatSpan(item.start, item.end)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {item.kind === 'event' && !item.event?.end_time
                  ? 'no end time given'
                  : formatMinutes(
                      Math.round((item.end.getTime() - item.start.getTime()) / 60000),
                    )}
              </Typography>
              {isNow && (
                <Typography variant="caption" color="success.main">
                  {formatMinutes(minutesLeft)} left
                </Typography>
              )}
              {isNext && (
                <Typography variant="caption" color="info.main">
                  starts in {formatMinutes(minutesUntil)}
                </Typography>
              )}
            </Stack>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {item.title}
            </Typography>
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
              {item.kind === 'block' && (
                <>
                  <Chip size="small" color="primary" variant="outlined" label="Task block" />
                  <Chip
                    size="small"
                    color={STATE_COLOR[state] ?? 'default'}
                    label={STATE_LABEL[state] ?? state}
                  />
                  {block?.project_title && (
                    <Typography variant="caption" color="text.secondary">
                      {block.project_title}
                    </Typography>
                  )}
                  {lateDue && (
                    <Chip
                      size="small"
                      color="error"
                      label={`Late — ends after the deadline (due ${lateDue})`}
                    />
                  )}
                </>
              )}
              {item.kind === 'commitment' && (
                <>
                  <Chip size="small" color="secondary" variant="outlined" label="Commitment" />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={String(item.occurrence?.state ?? 'planned')}
                  />
                </>
              )}
              {item.kind === 'event' && (
                <>
                  <Chip size="small" variant="outlined" label="Imported event" />
                  {item.event?.location && (
                    <Typography variant="caption" color="text.secondary">
                      {item.event.location}
                    </Typography>
                  )}
                </>
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block">
              {item.kind === 'block' && 'Yours — this page can move, keep or release it.'}
              {item.kind === 'commitment' &&
                'Yours, but it only moves in Commitments — nothing here reschedules it.'}
              {item.kind === 'event' &&
                'Someone else’s calendar, imported read-only. Nothing here can move it.'}
            </Typography>
            {block?.released_note && (
              <Typography variant="caption" color="text.secondary" display="block">
                Released: {block.released_note}
              </Typography>
            )}
          </Box>

          {item.kind === 'block' && block && (
            <Stack direction="row" spacing={0.5} alignItems="flex-start" flexWrap="wrap" useFlexGap>
              {state === 'planned' && (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={busyBlockId === block.id}
                  aria-label={`Keep ${blockTitle(block)}`}
                  onClick={() => keepBlock(block)}
                >
                  Keep
                </Button>
              )}
              {(state === 'planned' || state === 'kept') && (
                <>
                  <Button
                    size="small"
                    variant="text"
                    disabled={busyBlockId === block.id}
                    aria-label={`Release ${blockTitle(block)}`}
                    onClick={() => {
                      setReleaseNote('');
                      setReleaseError(null);
                      setReleaseTarget(block);
                    }}
                  >
                    Release
                  </Button>
                  <Tooltip title="Move or resize this block">
                    <span>
                      <Button
                        size="small"
                        variant="text"
                        startIcon={<EditIcon />}
                        disabled={busyBlockId === block.id}
                        aria-label={`Edit ${blockTitle(block)} span`}
                        onClick={() => openEdit(block)}
                      >
                        Edit
                      </Button>
                    </span>
                  </Tooltip>
                </>
              )}
              {/* Delete is PLANNED-only: a kept block is history, and the API
                  refuses to delete one. Offering the button would be a lie the
                  server then has to correct. */}
              {state === 'planned' && (
                <Button
                  size="small"
                  variant="text"
                  color="error"
                  startIcon={<DeleteIcon />}
                  disabled={busyBlockId === block.id}
                  aria-label={`Delete ${blockTitle(block)}`}
                  onClick={() => {
                    setDeleteError(null);
                    setDeleteTarget(block);
                  }}
                >
                  Delete
                </Button>
              )}
            </Stack>
          )}
        </Stack>
      </Paper>
    );
  };

  return (
    <PageShell maxWidth="wide">
      <Box sx={pageHeaderSx}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography variant="h3" component="h1">
              Planner
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Where task work meets the clock. Nothing here is written without
              showing you the plan first.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <TextField
              select
              size="small"
              label="Window"
              value={windowDays}
              onChange={(event) => setWindowDays(Number(event.target.value))}
              sx={{ minWidth: 120 }}
            >
              {WINDOW_CHOICES.map((choice) => (
                <MenuItem key={choice} value={choice}>
                  {choice} days
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => loadAgenda()}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              disabled={timezoneMissing}
              onClick={openPush}
            >
              Clear my afternoon
            </Button>
            <Button
              variant="contained"
              disabled={timezoneMissing}
              onClick={() => openPlanPreview(null)}
            >
              Schedule work
            </Button>
          </Stack>
        </Stack>
      </Box>

      {timezoneMissing && (
        <Alert severity="warning" sx={sectionGapSx}>
          <AlertTitle>No account timezone is set</AlertTitle>
          Planning is a question about future wall-clock times, so the server
          refuses to guess a zone — and this page will not invent one either.
          Set it in <strong>Profile → Hour classes → Account timezone</strong>,
          then come back. Until then your commitments cannot be merged into this
          agenda and no plan can be computed.
          <Box sx={{ mt: 1 }}>
            <Button size="small" variant="outlined" onClick={() => navigate('/profile')}>
              Open Profile
            </Button>
          </Box>
        </Alert>
      )}

      {loadErrors.map((message) => (
        <Alert key={message} severity="error" sx={{ mb: 1 }}>
          {message}
        </Alert>
      ))}

      {actionError && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {notice && (
        <Alert severity="success" sx={{ mb: 1 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      <Paper sx={panelPaddingSx}>
        <Tabs
          value={tab}
          onChange={(_event, value: 'agenda' | 'matrix') => setTab(value)}
          sx={{ mb: 2 }}
        >
          <Tab label="Agenda" value="agenda" />
          <Tab label="Matrix" value="matrix" />
        </Tabs>

        {tab === 'agenda' && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {formatDate(windowStart)} to {formatDate(addDays(windowStart, windowDays - 1))}.
              Task blocks are yours to move; commitments move only in
              Commitments; imported events are read-only.
            </Typography>

            {needsAnAnswer.length > 0 && (
              <Paper variant="outlined" sx={[panelPaddingSx, { mb: 2 }]}>
                <Typography variant="h6" component="h2">
                  Needs an answer ({needsAnAnswer.length})
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  These planned blocks are in the past and nothing has confirmed
                  them. Nothing promotes a block because its end time passed —
                  that would write history you never reported — so each one waits
                  here until you say whether it happened.
                </Typography>
                <Divider />
                {needsAnAnswer.map(renderAnswerRow)}
              </Paper>
            )}

            {loading ? (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Loading your agenda…
                </Typography>
              </Box>
            ) : (
              days.map((day) => (
                <Box key={day.key} sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {localDayKey(day.date) === localDayKey(now)
                      ? `Today — ${formatDate(day.date)}`
                      : formatDate(day.date)}
                  </Typography>
                  <Divider sx={{ mb: 1 }} />
                  {day.items.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Nothing scheduled.
                    </Typography>
                  ) : (
                    day.items.map(renderAgendaItem)
                  )}
                </Box>
              ))
            )}
          </Box>
        )}

        {tab === 'matrix' && (
          <EisenhowerMatrix
            tasks={matrixTasks}
            now={now}
            loading={tasksLoading}
            error={taskError}
            onSchedule={(task) => openPlanPreview(task)}
            onOpenInProjects={(task) =>
              navigate(task.project_id ? `/projects?project=${task.project_id}` : '/projects')
            }
          />
        )}
      </Paper>

      {/* ------------------------------------------------------------------ */}
      {/* Schedule: the preview is asked first and writes nothing.            */}
      {/* ------------------------------------------------------------------ */}
      <Dialog
        open={planOpen}
        onClose={() => setPlanOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Preview — nothing has been scheduled yet</DialogTitle>
        <DialogContent sx={dialogContentPaddingSx}>
          <Alert severity="info" sx={{ mb: 2 }}>
            This is a preview: no block has been created, moved or removed. The
            server recomputes the plan when you apply it, so if time has filled
            up in between, what lands can differ from what you see here.
          </Alert>
          {planScope && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              Scoped to one task: <strong>{planScope.title}</strong>
            </Typography>
          )}
          {planLoading && (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Working out where the work fits…
              </Typography>
            </Box>
          )}
          {planError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {planError}
            </Alert>
          )}
          {plan && !planLoading && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Horizon: {plan.horizon_days} days, to {formatInstant(plan.horizon_end)} (
                {plan.timezone}).
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                <Chip size="small" label={`Asked for ${formatMinutes(plan.requested_minutes)}`} />
                <Chip
                  size="small"
                  color="success"
                  label={`Placed ${formatMinutes(plan.placed_minutes)}`}
                />
                <Chip
                  size="small"
                  color={plan.unplaced_minutes > 0 ? 'error' : 'default'}
                  label={`Unplaced ${formatMinutes(plan.unplaced_minutes)}`}
                />
                <Chip
                  size="small"
                  color={plan.late_minutes > 0 ? 'warning' : 'default'}
                  label={`Late ${formatMinutes(plan.late_minutes)}`}
                />
              </Stack>

              {plan.unplaced_minutes > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  This plan does not fit. {formatMinutes(plan.unplaced_minutes)} of
                  work has nowhere to go — the per-task reasons below say why.
                </Alert>
              )}

              {(plan.warnings ?? []).map((warning) => (
                <Alert key={warning} severity="warning" sx={{ mb: 1 }}>
                  {warning}
                </Alert>
              ))}

              <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 1 }}>
                Blocks it would create ({plan.blocks.length})
              </Typography>
              {plan.blocks.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  None. Nothing would be scheduled.
                </Typography>
              ) : (
                renderPlanBlocks(plan.blocks)
              )}

              <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2 }}>
                Planned blocks it would replace ({plan.replaced_block_ids.length})
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Only unconfirmed planned blocks for the tasks being re-planned
                are replaced. Kept, pushed and released blocks are left alone.
              </Typography>
              {plan.replaced_block_ids.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  None.
                </Typography>
              ) : (
                <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                  {plan.replaced_block_ids.map((id) => (
                    <Typography key={id} variant="body2">
                      {describeBlockId(id)}
                    </Typography>
                  ))}
                </Stack>
              )}

              <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2, mb: 0.5 }}>
                Task by task
              </Typography>
              {plan.placements.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No task was schedulable — check estimates and statuses in
                  Projects.
                </Typography>
              ) : (
                renderPlacements(plan.placements)
              )}

              {(plan.unconfirmed_past_blocks ?? []).length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Needs an answer ({plan.unconfirmed_past_blocks.length})
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    These planned blocks are already in the past. They are not
                    counted against any estimate and nothing resolves them for
                    you — answer each one on the agenda.
                  </Typography>
                  {plan.unconfirmed_past_blocks.map((block) => (
                    <Typography key={block.id} variant="body2">
                      {describeBlockId(block.id)}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlanOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!plan || planLoading || planApplying}
            onClick={applyPlan}
          >
            Apply this plan
          </Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------------------------ */}
      {/* Push: same shape — see the cascade, then commit to it.              */}
      {/* ------------------------------------------------------------------ */}
      <Dialog open={pushOpen} onClose={() => setPushOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Clear my afternoon</DialogTitle>
        <DialogContent sx={dialogContentPaddingSx}>
          <DialogContentText sx={{ mb: 2 }}>
            Say when you stop being available. Your task blocks in that window
            are displaced and re-packed after it. Nothing moves until you press
            “Apply this push”.
          </DialogContentText>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 1 }}>
            {/* Editing either end discards the preview: Apply sends whatever
                is in these fields, so a stale cascade on screen would describe
                a push the user is no longer asking for. */}
            <TextField
              label="Unavailable from"
              type="datetime-local"
              value={pushFrom}
              onChange={(event) => {
                setPushFrom(event.target.value);
                setPush(null);
              }}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Until (optional)"
              type="datetime-local"
              value={pushUntil}
              onChange={(event) => {
                setPushUntil(event.target.value);
                setPush(null);
              }}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            Leave “until” empty to clear the rest of your local day.
          </Typography>
          <Button variant="outlined" onClick={previewPush} disabled={pushLoading}>
            Preview the push
          </Button>

          {pushLoading && (
            <Box display="flex" alignItems="center" gap={1} sx={{ mt: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Working out the cascade…
              </Typography>
            </Box>
          )}
          {pushError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {pushError}
            </Alert>
          )}

          {push && !pushLoading && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Preview only — nothing has moved yet. Window:{' '}
                {formatInstant(push.from_at)} to {formatInstant(push.until)} ({push.timezone}).
              </Alert>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                <Chip size="small" label={`Displaced ${formatMinutes(push.displaced_minutes)}`} />
                <Chip
                  size="small"
                  color="success"
                  label={`Re-placed ${formatMinutes(push.replaced_minutes)}`}
                />
                <Chip
                  size="small"
                  color={push.unplaced_minutes > 0 ? 'error' : 'default'}
                  label={`Unplaced ${formatMinutes(push.unplaced_minutes)}`}
                />
              </Stack>
              {push.unplaced_minutes > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {formatMinutes(push.unplaced_minutes)} of displaced work has
                  nowhere to land inside the horizon. It is not silently dropped
                  — those minutes stay on the task, unscheduled.
                </Alert>
              )}

              {(push.warnings ?? []).map((warning) => (
                <Alert key={warning} severity="warning" sx={{ mb: 1 }}>
                  {warning}
                </Alert>
              ))}

              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Blocks it would displace ({push.displaced_block_ids.length})
              </Typography>
              {push.displaced_block_ids.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  None.
                </Typography>
              ) : (
                <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                  {push.displaced_block_ids.map((id) => (
                    <Typography key={id} variant="body2">
                      {describeBlockId(id)}
                    </Typography>
                  ))}
                </Stack>
              )}

              <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2 }}>
                Blocks it would shorten ({push.shortened_blocks.length})
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                A block straddling the boundary is trimmed back to it and stays
                planned — the part already under way really did happen then.
              </Typography>
              {push.shortened_blocks.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  None.
                </Typography>
              ) : (
                <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                  {push.shortened_blocks.map((shortened) => (
                    <Typography key={shortened.block_id} variant="body2">
                      {describeBlockId(shortened.block_id)} → new end{' '}
                      {formatInstant(shortened.new_end_at)} ({formatMinutes(shortened.minutes)})
                    </Typography>
                  ))}
                </Stack>
              )}

              <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2 }}>
                Replacement blocks ({push.blocks.length})
              </Typography>
              {push.blocks.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  None.
                </Typography>
              ) : (
                renderPlanBlocks(push.blocks)
              )}

              <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2 }}>
                Commitments in this window — NOT moved (
                {push.commitments_in_window.length})
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                A push never touches a commitment. They move only through
                Commitments, which has its own carryover rules and its own audit
                trail; rewriting one from here would be exactly the invisible
                change this app refuses to make.
              </Typography>
              {push.commitments_in_window.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  None in this window.
                </Typography>
              ) : (
                <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                  {push.commitments_in_window.map((pointer) => (
                    <Typography key={pointer.occurrence_id} variant="body2">
                      {pointer.title ?? `Commitment #${pointer.commitment_id}`} ·{' '}
                      {formatSpan(
                        new Date(pointer.planned_start_at),
                        new Date(pointer.planned_end_at),
                      )}{' '}
                      — not moved
                      {pointer.reschedulable
                        ? ' (reschedule it in Commitments if you need to)'
                        : ' (this one is not reschedulable)'}
                    </Typography>
                  ))}
                </Stack>
              )}

              {push.placements.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Task by task
                  </Typography>
                  {renderPlacements(push.placements)}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPushOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!push || pushLoading || pushApplying}
            onClick={applyPush}
          >
            Apply this push
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit one span. */}
      <Dialog open={editTarget !== null} onClose={() => setEditTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Move {editTarget ? blockTitle(editTarget) : 'block'}</DialogTitle>
        <DialogContent sx={dialogContentPaddingSx}>
          <DialogContentText sx={{ mb: 2 }}>
            Both ends are restated together — there is no half-edit of a span.
            The server checks the new span against your other blocks and your
            planned commitments.
          </DialogContentText>
          <Stack spacing={2}>
            <TextField
              label="Starts"
              type="datetime-local"
              value={editStart}
              onChange={(event) => setEditStart(event.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Ends"
              type="datetime-local"
              value={editEnd}
              onChange={(event) => setEditEnd(event.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Stack>
          {editError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {editError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTarget(null)}>Cancel</Button>
          <Button variant="contained" onClick={submitEdit}>
            Save span
          </Button>
        </DialogActions>
      </Dialog>

      {/* Release, with the optional note the API accepts. */}
      <Dialog
        open={releaseTarget !== null}
        onClose={() => setReleaseTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Release {releaseTarget ? blockTitle(releaseTarget) : 'block'}</DialogTitle>
        <DialogContent sx={dialogContentPaddingSx}>
          <DialogContentText sx={{ mb: 2 }}>
            Releasing gives the block’s minutes back to the task, whether the
            work finished early or simply is not happening then. The block stays
            on the record as released.
          </DialogContentText>
          <TextField
            label="Note (optional)"
            value={releaseNote}
            onChange={(event) => setReleaseNote(event.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
          {releaseError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {releaseError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReleaseTarget(null)}>Cancel</Button>
          <Button variant="contained" onClick={submitRelease}>
            Release it
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete — planned blocks only. */}
      <Dialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Unschedule this block?</DialogTitle>
        <DialogContent sx={dialogContentPaddingSx}>
          <DialogContentText>
            {deleteTarget ? blockTitle(deleteTarget) : ''} —{' '}
            {deleteTarget
              ? formatSpan(new Date(deleteTarget.start_at), new Date(deleteTarget.end_at))
              : ''}
            . Its minutes go back to the task. Only a planned block can be
            deleted; a kept one is history and is released instead.
          </DialogContentText>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={submitDelete}>
            Delete block
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default Planner;
